import React from 'react';
import { motion } from 'framer-motion';
import { Mail, User, Sparkles, ExternalLink, Trash2, Bookmark, Share2, Lock } from 'lucide-react';
import { useLanguage } from '../LanguageContext';
import { t } from '../translations';

/**
 * DesktopArticleCard renderar ett artikelkort i desktop-layout med
 * automatisk paywall-overlay vid uppnådd månadskvot om sammanfattning saknas.
 */
const DesktopArticleCard = ({ 
  item, 
  markAsRead, 
  deleteItem, 
  renderAISummary, 
  saveFromInbox, 
  onHide, 
  isLibraryView = false,
  canGenerateSummary = true,
  usageStats,
  consumeSummary, // Används för att aktivera paywall i App.js
  onOpenPaywall   // Valfri direkt callback till betalvyn
}) => {
  const { language } = useLanguage();
  
  const executeArchive = (e) => {
    e?.stopPropagation();
    onHide(item.id); 
    saveFromInbox(item);
  };

  const executeDelete = (e) => {
    e?.stopPropagation();
    onHide(item.id); 
    deleteItem(item.id);
  };

  const handlePaywallClick = () => {
    if (onOpenPaywall) {
      onOpenPaywall();
    } else if (consumeSummary) {
      // consumeSummary i App.js aktiverar automatiskt setForcePaywall(true) när kvoten är slut
      consumeSummary();
    }
  };

  // Har artikeln redan en färdig AI-sammanfattning?
  const hasExistingSummary = Boolean(item.ai_summary || item.summary);

  // Paywall visas ENDAST om artikeln saknar sammanfattning och användaren inte har kvot att generera ny
  const showPaywall = !hasExistingSummary && !canGenerateSummary && !isLibraryView;

  // Hämta korrekt månadskvot från usageStats (standard: 600)
  const maxLimit = usageStats?.limits?.summaries || 600;

  return (
    <motion.div 
      layout
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, height: 0, marginBottom: 0, overflow: 'hidden' }}
      transition={{ duration: 0.3 }}
      className="relative mb-6 w-full box-border group"
    >
      <div className="rounded-3xl border border-slate-200 text-left relative z-10 flex flex-col overflow-hidden bg-white shadow-sm hover:shadow-md transition-shadow duration-300">
        <div className="p-8 border-b border-slate-50 w-full box-border relative">
            
            {/* Hover-knappar (Visas bara på Desktop vid hover) */}
            <div className="absolute top-8 right-8 flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-20">
                <button 
                    onClick={executeDelete} 
                    className="text-slate-400 hover:text-red-600 hover:bg-red-50 p-2 rounded-full transition-all bg-white shadow-sm border border-slate-100"
                    title={t(language, 'inbox_delete')}
                >
                    <Trash2 size={20} />
                </button>
                {!item.is_read && !isLibraryView && (
                    <button 
                        onClick={() => markAsRead(item.id)} 
                        className="text-slate-400 hover:text-blue-600 hover:bg-blue-50 p-2 rounded-full transition-all bg-white shadow-sm border border-slate-100"
                        title={t(language, 'inbox_mark_as_read')}
                    >
                        <Mail size={20} />
                    </button>
                )}
                {isLibraryView && (
                    <button 
                        onClick={() => {/* Framtida dela-funktion */}} 
                        className="text-slate-400 hover:text-blue-600 hover:bg-blue-50 p-2 rounded-full transition-all bg-white shadow-sm border border-slate-100"
                        title={t(language, 'library_share')}
                    >
                        <Share2 size={20} />
                    </button>
                )}
            </div>

            <div className="flex justify-between items-start mb-4 gap-2">
                <div className="flex items-center gap-2 min-w-0 flex-wrap">
                    <span className="text-[11px] font-bold text-slate-400 uppercase shrink-0">
                      {new Date(item.created_at || Date.now()).toLocaleDateString()}
                    </span>
                    {!item.is_read && !isLibraryView && (
                      <span className="bg-blue-600 text-white text-[10px] font-bold px-2.5 py-0.5 rounded-full uppercase tracking-wider">
                        {t(language, 'inbox_new')}
                      </span>
                    )}
                </div>
            </div>
            
            <h3 className={`text-2xl font-serif mb-3 leading-tight break-words min-w-0 pr-24 ${item.is_read || isLibraryView ? 'text-slate-600 font-medium' : 'text-slate-900 font-bold'}`}>
                {item.article_title || item.title}
            </h3>
            
            <div className="space-y-2 mb-4">
                <p className="text-sm font-medium italic flex items-start gap-2 text-slate-500">
                  <User size={16} className="shrink-0 mt-0.5"/> 
                  <span className="leading-relaxed line-clamp-2 break-words">
                    {item.authors || item.author || t(language, 'inbox_unknown_author')}
                  </span>
                </p>
                <div className="text-xs uppercase tracking-tighter font-sans flex items-center gap-2 text-slate-400">
                  <span className="truncate min-w-0">
                    {item.venue || item.journal || t(language, 'inbox_unknown_journal')}
                  </span>
                </div>
            </div>
        </div>
        
        {/* AI-summering Sektion */}
        <div className="p-8 border-t border-indigo-100 bg-indigo-50/50 border-l-4 border-l-indigo-300 w-full box-border relative">
            <div className="flex items-center gap-2 text-[11px] font-bold text-indigo-500 uppercase tracking-widest mb-4">
              <Sparkles size={16} className="shrink-0"/> {t(language, 'inbox_ai_summary')}
            </div>

            {showPaywall ? (
              /* --- PAYWALL LÄGE (Bara om sammanfattning saknas och kvoten är slut) --- */
              <div className="relative">
                <div className="leading-relaxed text-lg font-serif break-words text-slate-700 blur-[4px] select-none opacity-40">
                  {t(language, 'card_blurred_placeholder')}
                </div>
                
                <div className="absolute inset-0 flex flex-col items-center justify-center z-10">
                  <div className="bg-white/95 backdrop-blur-sm p-6 rounded-2xl shadow-xl border border-indigo-100 max-w-sm w-full text-center transform transition-transform hover:scale-105">
                    <div className="w-12 h-12 bg-indigo-100 text-indigo-600 rounded-full flex items-center justify-center mx-auto mb-3">
                      <Lock size={24} />
                    </div>
                    <h4 className="text-lg font-bold text-slate-800 mb-2">{t(language, 'card_quota_title')}</h4>
                    <p className="text-sm text-slate-600 mb-5 leading-relaxed">
                      {t(language, 'card_quota_desc_desktop', { maxLimit })}
                    </p>
                    <button 
                      onClick={handlePaywallClick}
                      className="w-full bg-slate-900 text-white px-4 py-3 rounded-xl font-bold hover:bg-slate-800 transition-colors shadow-md active:scale-95"
                    >
                      {t(language, 'card_quota_btn')}
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              /* --- NORMALT LÄGE: Visar alltid den sparade AI-sammanfattningen --- */
              <>
                <div className="leading-relaxed whitespace-pre-line text-lg font-serif break-words text-slate-700">
                    {renderAISummary(item.ai_summary || item.summary || item.abstract, language)}
                </div>
                
                <div className="pt-6 mt-6 border-t border-indigo-100/50 flex flex-wrap justify-between items-center gap-3 w-full box-border">
                    <a 
                      href={item.doi ? item.doi : item.article_url} 
                      target="_blank" 
                      rel="noreferrer" 
                      className="text-xs font-bold text-indigo-600 hover:text-indigo-800 uppercase flex items-center gap-1 min-w-0 flex-1 truncate"
                    >
                      <span className="truncate">{t(language, 'inbox_read_original')}</span> <ExternalLink size={14} className="shrink-0"/>
                    </a>
                    
                    {!isLibraryView && (
                      <button 
                        onClick={executeArchive}
                        className="text-xs font-bold text-emerald-600 hover:text-emerald-700 bg-emerald-50 hover:bg-emerald-100 px-4 py-2.5 rounded-full uppercase flex items-center gap-1 transition-colors shrink-0"
                      >
                        <Bookmark size={16} className="shrink-0"/> <span>{t(language, 'inbox_archive')}</span>
                      </button>
                    )}
                </div>
              </>
            )}
        </div>
      </div>
    </motion.div>
  );
};

export default DesktopArticleCard;